import React, { useState, useEffect } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import LeftMenu from "../components/LeftMenu";
import apiService from "../services/api";
import "./WebPlayerMultiView.css";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerMultiView = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const profile =
    location.state?.profile ||
    JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist =
    location.state?.playlist ||
    JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");

  const [selectedLayout, setSelectedLayout] = useState("quad");
  const [videoSlots, setVideoSlots] = useState([
    { id: 1, channel: null },
    { id: 2, channel: null },
    { id: 3, channel: null },
    { id: 4, channel: null },
  ]);
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreChannels, setHasMoreChannels] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [mutedSlots, setMutedSlots] = useState({});

  // Check for profile and redirect if not present
  useEffect(() => {
    if (!profile || !profile.id) {
      navigate("/web-player/profiles");

      return;
    }

    // Check for webPlayerToken
    const webPlayerToken = webPlayerStorage.getToken();

    if (!webPlayerToken) {
      navigate("/web-player");
    }
  }, [profile, navigate]);

  const layouts = [
    { id: "dual", slots: 2 },
    { id: "triple", slots: 3 },
    { id: "quad", slots: 4 },
    { id: "pip", slots: 3 },
  ];

  const handleLayoutChange = (layoutId) => {
    setSelectedLayout(layoutId);

    // Adjust video slots based on layout
    const layout = layouts.find((l) => l.id === layoutId);

    if (layout) {
      const newSlots = [];

      for (let i = 0; i < layout.slots; i++) {
        newSlots.push({
          id: i + 1,
          channel: videoSlots[i]?.channel || null,
        });
      }
      setVideoSlots(newSlots);
    }
  };

  const handleSlotClick = (slotId) => {
    setSelectedSlotId(slotId);
    setShowChannelSelector(true);
    loadCategories();
  };

  const loadCategories = async () => {
    try {
      const response = await apiService.webPlayerRequest(
        "/v2/live-tv/categories"
      );
      const categoriesArray = response?.data || response;

      if (Array.isArray(categoriesArray)) {
        const categoriesData = categoriesArray.map((cat) => ({
          id: cat.categoryId || cat.id,
          name: cat.categoryName || cat.name,
          count: cat.itemCount || 0,
        }));

        setCategories(categoriesData);

        if (categoriesData.length > 0 && !selectedCategory) {
          setSelectedCategory(categoriesData[0].id);
          loadChannels(categoriesData[0].id, 1, false);
        }
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const loadChannels = async (categoryId, page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
        setChannels([]);
        setCurrentPage(1);
        setHasMoreChannels(true);
      } else {
        setIsLoadingMore(true);
      }

      let url = `/v2/live-tv?page=${page}&limit=100`;

      if (categoryId) {
        url += `&categories=${categoryId}`;
      }

      const response = await apiService.webPlayerRequest(url);
      const channelsArray = response?.data || response?.items || response;

      if (Array.isArray(channelsArray)) {
        const channelsData = channelsArray.map((channel, index) => ({
          uuid:
            channel.mediaId?.toString() ||
            channel.id?.toString() ||
            `channel-${page}-${index}`,
          title: channel.name,
          logo:
            channel.logoPath ||
            channel.posterPath ||
            channel.logo ||
            channel.stream_icon,
          streamUrl: channel.streamUrl || channel.stream_url,
        }));

        if (append) {
          setChannels((prev) => [...prev, ...channelsData]);
        } else {
          setChannels(channelsData);
        }

        // If we got less than 100 channels, there are no more
        if (channelsArray.length < 100) {
          setHasMoreChannels(false);
        }

        setCurrentPage(page);
      } else {
        setHasMoreChannels(false);
      }
    } catch (error) {
      console.error("Error loading channels:", error);
      if (!append) {
        setChannels([]);
      }
      setHasMoreChannels(false);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreChannels = () => {
    if (!isLoadingMore && hasMoreChannels && selectedCategory) {
      loadChannels(selectedCategory, currentPage + 1, true);
    }
  };

  const handleChannelsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Load more when user is 200px from the bottom
    if (scrollHeight - scrollTop <= clientHeight + 200) {
      loadMoreChannels();
    }
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
    setHasMoreChannels(true);
    loadChannels(categoryId, 1, false);
  };

  const handleChannelSelect = (channel) => {
    const updatedSlots = [...videoSlots];
    const slotIndex = updatedSlots.findIndex(
      (slot) => slot.id === selectedSlotId
    );

    if (slotIndex !== -1) {
      updatedSlots[slotIndex].channel = channel;
      setVideoSlots(updatedSlots);
    }
    setShowChannelSelector(false);
    setSelectedSlotId(null);
  };

  const closeChannelSelector = () => {
    setShowChannelSelector(false);
    setSelectedSlotId(null);
  };

  const getGridClassName = () => {
    switch (selectedLayout) {
      case "dual":
        return "multiview-grid-dual";
      case "triple":
        return "multiview-grid-triple";
      case "quad":
        return "multiview-grid-quad";
      case "pip":
        return "multiview-grid-pip";
      default:
        return "multiview-grid-quad";
    }
  };

  const getActiveSlots = () => {
    const layout = layouts.find((l) => l.id === selectedLayout);

    return videoSlots.slice(0, layout?.slots || 4);
  };

  const toggleAudio = (slotId, e) => {
    e.stopPropagation();
    setMutedSlots((prev) => ({
      ...prev,
      [slotId]: !prev[slotId],
    }));
  };

  const renderVideoSlot = (slot) => {
    if (slot.channel) {
      const isMuted = mutedSlots[slot.id] !== false; // Default to muted
      const isHovered = hoveredSlot === slot.id;

      return (
        <div
          aria-label={`${slot.channel.title} video`}
          className="multiview-video"
          role="region"
          onMouseEnter={() => setHoveredSlot(slot.id)}
          onMouseLeave={() => setHoveredSlot(null)}
        >
          {slot.channel.logo && (
            <img
              alt={slot.channel.title}
              className="multiview-channel-logo"
              src={slot.channel.logo}
            />
          )}
          <video
            className="multiview-player"
            muted={!isHovered || isMuted}
            style={{ display: "none" }}
          />

          {/* Audio Control Button */}
          {isHovered && (
            <button
              className="multiview-audio-control"
              onClick={(e) => toggleAudio(slot.id, e)}
            >
              <i
                className={`fa-solid ${isMuted ? "fa-volume-xmark" : "fa-volume-high"}`}
              />
            </button>
          )}

          <div className="multiview-channel-info">
            <span>{slot.channel.title}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="multiview-empty">
        <i className="fa-solid fa-circle-plus multiview-add-icon" />
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      {/* Left Menu */}
      <LeftMenu activeMenuItem="live" playlist={playlist} profile={profile} />

      <div className="ml-20 flex flex-col min-h-screen lg:h-screen">
        {/* Header */}
        <div className="multiview-header">
          <div className="multiview-header-left">
            <button
              className="multiview-back-btn"
              onClick={() =>
                navigate("/web-player/live-tv", {
                  state: { profile, playlist },
                })
              }
            >
              <i className="fa-solid fa-arrow-left" />
            </button>
            <h1 className="multiview-title">MultiView</h1>
          </div>
          <div className="multiview-header-right">
            <span className="multiview-subtitle">
              Select up to{" "}
              {layouts.find((l) => l.id === selectedLayout)?.slots || 4}{" "}
              channels
            </span>
          </div>
        </div>

        {/* Video Grid */}
        <div className="multiview-container">
          <div className={`multiview-grid ${getGridClassName()}`}>
            {selectedLayout === "triple" ? (
              <>
                {/* Top two slots */}
                <div className="multiview-grid-triple-top">
                  {videoSlots.slice(0, 2).map((slot, index) => (
                    <div
                      className="multiview-slot"
                      key={slot.id}
                      role="button"
                      style={{
                        animationDelay: `${index * 0.1}s`,
                      }}
                      tabIndex={0}
                      onClick={() => handleSlotClick(slot.id)}
                      onKeyDown={activateOnEnterOrSpace}
                    >
                      {renderVideoSlot(slot)}
                    </div>
                  ))}
                </div>
                {/* Bottom single slot (centered, same size as top slots) */}
                <div className="multiview-grid-triple-bottom">
                  <div
                    className="multiview-slot"
                    key={videoSlots[2].id}
                    role="button"
                    style={{
                      animationDelay: "0.2s",
                    }}
                    tabIndex={0}
                    onClick={() => handleSlotClick(videoSlots[2].id)}
                    onKeyDown={activateOnEnterOrSpace}
                  >
                    {renderVideoSlot(videoSlots[2])}
                  </div>
                </div>
              </>
            ) : selectedLayout === "pip" ? (
              <>
                {/* First slot centered on top */}
                <div
                  className="multiview-slot"
                  key={videoSlots[0].id}
                  role="button"
                  style={{
                    animationDelay: "0s",
                  }}
                  tabIndex={0}
                  onClick={() => handleSlotClick(videoSlots[0].id)}
                  onKeyDown={activateOnEnterOrSpace}
                >
                  {renderVideoSlot(videoSlots[0])}
                </div>
                {/* Bottom two slots */}
                <div className="multiview-grid-pip-bottom">
                  {videoSlots.slice(1, 3).map((slot, index) => (
                    <div
                      className="multiview-slot"
                      key={slot.id}
                      role="button"
                      style={{
                        animationDelay: `${(index + 1) * 0.1}s`,
                      }}
                      tabIndex={0}
                      onClick={() => handleSlotClick(slot.id)}
                      onKeyDown={activateOnEnterOrSpace}
                    >
                      {renderVideoSlot(slot)}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              getActiveSlots().map((slot, index) => (
                <div
                  className="multiview-slot"
                  key={slot.id}
                  role="button"
                  style={{
                    animationDelay: `${index * 0.1}s`,
                  }}
                  tabIndex={0}
                  onClick={() => handleSlotClick(slot.id)}
                  onKeyDown={activateOnEnterOrSpace}
                >
                  {slot.channel ? (
                    <div className="multiview-video">
                      <video className="multiview-player" />
                      <div className="multiview-channel-info">
                        <span>{slot.channel.name}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="multiview-empty">
                      <i className="fa-solid fa-circle-plus multiview-add-icon" />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Layout Selector */}
        <div className="multiview-layout-selector">
          <div className="multiview-layouts">
            {layouts.map((layout) => (
              <button
                aria-label={`${layout.slots} view${layout.slots > 1 ? "s" : ""}`}
                className={`multiview-layout-btn ${
                  selectedLayout === layout.id ? "active" : ""
                }`}
                key={layout.id}
                onClick={() => handleLayoutChange(layout.id)}
              >
                {layout.id === "dual" && (
                  <div className="layout-icon-grid">
                    <div className="layout-icon-row">
                      <i className="fa-solid fa-square" />
                      <i className="fa-solid fa-square" />
                    </div>
                  </div>
                )}
                {layout.id === "triple" && (
                  <div className="layout-icon-grid">
                    <div className="layout-icon-row">
                      <i className="fa-solid fa-square" />
                      <i className="fa-solid fa-square" />
                    </div>
                    <div className="layout-icon-row">
                      <i className="fa-solid fa-square" />
                    </div>
                  </div>
                )}
                {layout.id === "quad" && (
                  <div className="layout-icon-grid">
                    <div className="layout-icon-row">
                      <i className="fa-solid fa-square" />
                      <i className="fa-solid fa-square" />
                    </div>
                    <div className="layout-icon-row">
                      <i className="fa-solid fa-square" />
                      <i className="fa-solid fa-square" />
                    </div>
                  </div>
                )}
                {layout.id === "pip" && (
                  <div className="layout-icon-grid">
                    <div className="layout-icon-row">
                      <i className="fa-solid fa-square" />
                    </div>
                    <div className="layout-icon-row">
                      <i className="fa-solid fa-square" />
                      <i className="fa-solid fa-square" />
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Dashboard Button */}
      <button
        className="fixed bottom-4 right-4 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        onClick={() => {
          // Clear all session data
          webPlayerStorage.clearAll();
          // Redirect to dashboard
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </button>

      {/* Channel Selector Overlay */}
      {showChannelSelector && (
        <div
          className="channel-selector-overlay"
          role="presentation"
          onClick={closeChannelSelector}
        >
          <div
            className="channel-selector-modal"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="channel-selector-header">
              <h2>Select Channel</h2>
              <button
                className="channel-selector-close"
                onClick={closeChannelSelector}
              >
                <i className="fa-solid fa-times" />
              </button>
            </div>
            <div className="channel-selector-content">
              {/* Categories List */}
              <div className="channel-selector-categories">
                {categories.map((category) => (
                  <button
                    className={`category-item ${selectedCategory === category.id ? "active" : ""}`}
                    key={category.id}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    <span className="category-name">{category.name}</span>
                    <span className="category-count">
                      {category.count} channels
                    </span>
                  </button>
                ))}
              </div>

              {/* Channels Grid */}
              <div
                className="channel-selector-channels"
                onScroll={handleChannelsScroll}
              >
                {loading ? (
                  <div className="channels-loading">
                    <div className="loading-spinner" />
                  </div>
                ) : (
                  <div className="channels-grid">
                    {channels.map((channel) => (
                      <button
                        className="channel-item"
                        key={channel.uuid}
                        onClick={() => handleChannelSelect(channel)}
                      >
                        {channel.logo ? (
                          <img
                            alt={channel.title}
                            className="channel-logo"
                            src={channel.logo}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className="channel-fallback"
                          style={{ display: channel.logo ? "none" : "flex" }}
                        >
                          <span>{channel.title?.charAt(0) || "?"}</span>
                        </div>
                        <span className="channel-name">{channel.title}</span>
                      </button>
                    ))}
                    {isLoadingMore && (
                      <div className="channel-item loading-more">
                        <div className="loading-spinner-small" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebPlayerMultiView;
